import {ApiResponseError} from '../shared/errors.js';
import * as config from '../../config/app.config.json';
import {mapConfig} from '../config/map-config.js';

class Api {
  constructor(environment, apiToken) {
    const envConfig = config.environments[environment];
    this.token = apiToken || envConfig.apiToken;
    this.baseUrl = `${envConfig.baseUrl}${envConfig.apiEndpoint}`;
    this.headers = {
      Authorization: `Token ${this.token}`,
      Accept: 'application/json'
    };
  }

  fetchClosestDealers(options, callback) {
    const params = this._getClosestDealersParams(options);
    const url = this._getUrl('dealers.json', params);
    this._get(url, callback);
  }

  fetchDealers(options, callback) {
    const params = this._getDealersParams(options);
    const url = this._getUrl('dealers/within_bounds.json', params);
    this._get(url, dealers => {
      // Workaround: Filter duplicate attributes. Needs to be fixed in the backend.
      dealers.forEach(dealer => {
        if (dealer.dealer_attributes) {
          dealer.dealer_attributes = dealer.dealer_attributes.filter(
            (v, i, a) => a.indexOf(v) === i
          );
        }
      });
      return callback(dealers);
    });
  }

  fetchAttributes(marketIds, businessUnitId, network, callback) {
    const params = this._getAttributesParams(
      marketIds,
      businessUnitId,
      network
    );
    const url = this._getUrl('dealer_attributes.json', params);
    this._get(url, callback);
  }

  fetchSettings(marketIds, businessUnitId, brandId, network, callback) {
    const params = this._getSettingsParams(
      marketIds,
      businessUnitId,
      brandId,
      network
    );
    const url = this._getUrl('settings.json', params);
    this._get(url, callback);
  }

  fetchDealerSuggestions(options, callback) {
    const params = this._getDealerSuggestionsParams(options);
    const url = this._getUrl('dealers/search.json', params);
    this._get(url, callback);
  }

  _get(url, callback) {
    const request = this._getXMLHttpRequest();

    request.onreadystatechange = () => {
      if (request.readyState === 4 && request.status !== 200) {
        throw new ApiResponseError(
          'Error while fetching data from API',
          request.status
        );
      }
      if (request.readyState === 4 && request.status === 200) {
        return callback(JSON.parse(request.response));
      }

      return null;
    };

    request.open('GET', url, true);

    // for-in instead of Object.keys for IE11 compatibility without a polyfill
    for (const key in this.headers) {
      if (Object.prototype.hasOwnProperty.call(this.headers, key)) {
        request.setRequestHeader(key, this.headers[key]);
      }
    }

    request.send(null);
  }

  _getXMLHttpRequest() {
    let request = null;

    if (window.XMLHttpRequest) {
      request = new XMLHttpRequest();
    } else if (window.ActiveXObject) {
      // IE11
      request = new ActiveXObject('Msxml2.XMLHTTP');
    }

    return request;
  }

  _getClosestDealersParams({
    attributes,
    latLng,
    brandId,
    marketIds,
    businessUnitId,
    network,
    maxDealers,
    featuredDealersOnly
  }) {
    const params = {
      'location[lat]': latLng[0],
      'location[lon]': latLng[1],
      per_page: maxDealers || 3,
      only_featured: featuredDealersOnly
    };

    if (marketIds.length > 0) {
      params.market_key = marketIds.join(',');
    }

    if (network) {
      params.network_names = network;
    }

    if (brandId) {
      params.brand_key = brandId;
    }

    if (businessUnitId) {
      params.business_unit_key = businessUnitId;
    }

    if (attributes && attributes.length) {
      params['attributes[]'] = attributes.join(',');
    }

    return params;
  }

  _getDealersParams({
    attributes,
    bounds,
    brandId,
    marketIds,
    businessUnitId,
    network,
    featuredDealersOnly
  }) {
    const params = {
      'bbox[sw_lat]': bounds.southWest.lat,
      'bbox[sw_lon]': bounds.southWest.lng,
      'bbox[ne_lat]': bounds.northEast.lat,
      'bbox[ne_lon]': bounds.northEast.lng,
      max_detailed: 8000,
      max_all_details: mapConfig.DETAILED_MARKER_THRESHOLD_COUNT
    };

    if (marketIds.length > 0) {
      params.market_key = marketIds.join(',');
    }

    if (network) {
      params.network_names = network;
    }

    if (brandId) {
      params.brand_key = brandId;
    }

    if (businessUnitId) {
      params.business_unit_key = businessUnitId;
    }

    if (attributes && attributes.length) {
      params.attributes = attributes.join(',');
    }

    if (brandId && businessUnitId) {
      params.only_featured = featuredDealersOnly;
    }

    return params;
  }

  _getAttributesParams(marketIds, businessUnitId, network) {
    const params = {
      market_key: marketIds.join(',')
    };

    if (businessUnitId) {
      params.business_unit_key = businessUnitId;
    }

    if (network) {
      params.network_names = network;
    }

    return params;
  }

  _getSettingsParams(marketIds, businessUnitId, brandId, network) {
    const params = {
      market_key: marketIds.join(',')
    };

    if (businessUnitId) {
      params.business_unit_key = businessUnitId;
    }

    if (brandId) {
      params.brand_key = brandId;
    }

    if (network) {
      params.network_names = network;
    }

    return params;
  }

  _getDealerSuggestionsParams(options) {
    const params = {
      'location[lat]': options.latLng[0],
      'location[lon]': options.latLng[1],
      search_text: options.searchText,
      market_key: options.marketIds.join(','),
      limit: options.limit,
      only_featured: options.featuredDealersOnly
    };

    if (options.attributes && options.attributes.length) {
      params.attributes = options.attributes.join(',');
    }

    if (options.businessUnitId) {
      params.business_unit_key = options.businessUnitId;
    }

    if (options.brandId) {
      params.brand_key = options.brandId;
    }

    if (options.network) {
      params.network_names = options.network;
    }

    if (options.brandId && options.usinessUnitId) {
      params.only_featured = options.only_featured;
    }
    return params;
  }

  _getUrl(endpoint, params) {
    const queryParams = [];

    // for-in instead of Object.keys for IE11 compatibility without a polyfill
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const param = `${encodeURIComponent(key)}=${encodeURIComponent(
          params[key]
        )}`;
        queryParams.push(param);
      }
    }

    return `${this.baseUrl}/${endpoint}?${queryParams.join('&')}`;
  }
}

export default Api;
